/**
 * Native Platform Utilities for Capacitor
 * Handles iOS/Android specific functionality
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';

// Check if running on native platform
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'ios', 'android', or 'web'
export const isIOS = () => getPlatform() === 'ios';
export const isAndroid = () => getPlatform() === 'android';
export const isWeb = () => getPlatform() === 'web';

/**
 * Initialize native app features
 * Call this in App.js on mount
 */
export const initializeNativeApp = async () => {
  if (!isNative()) return;

  try {
    // Hide splash screen after app loads
    await SplashScreen.hide();

    // Set status bar style
    await StatusBar.setStyle({ style: Style.Dark });
    
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#1a1a2e' });
    }

    // Set up app state listeners
    setupAppStateListeners();

    // Request push notification permissions
    await requestPushPermissions();

    console.log('[Native] App initialized successfully');
  } catch (error) {
    console.error('[Native] Initialization error:', error);
  }
};

/**
 * App state listeners (foreground/background)
 */
const setupAppStateListeners = () => {
  App.addListener('appStateChange', ({ isActive }) => {
    console.log('[Native] App state changed. Is active:', isActive);
    // You can pause/resume audio, refresh data, etc.
  });

  App.addListener('backButton', () => {
    // Handle Android back button
    console.log('[Native] Back button pressed');
  });

  App.addListener('appUrlOpen', (data) => {
    // Handle deep links
    console.log('[Native] App opened with URL:', data.url);
  });
};

/**
 * Haptic feedback
 */
export const hapticLight = async () => {
  if (!isNative()) return;
  await Haptics.impact({ style: ImpactStyle.Light });
};

export const hapticMedium = async () => {
  if (!isNative()) return;
  await Haptics.impact({ style: ImpactStyle.Medium });
};

export const hapticHeavy = async () => {
  if (!isNative()) return;
  await Haptics.impact({ style: ImpactStyle.Heavy });
};

export const hapticSuccess = async () => {
  if (!isNative()) return;
  await Haptics.notification({ type: 'SUCCESS' });
};

/**
 * Local Storage using Capacitor Preferences (more reliable on native)
 */
export const setStorageItem = async (key, value) => {
  if (isNative()) {
    await Preferences.set({ key, value: JSON.stringify(value) });
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const getStorageItem = async (key) => {
  if (isNative()) {
    const { value } = await Preferences.get({ key });
    return value ? JSON.parse(value) : null;
  } else {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }
};

export const removeStorageItem = async (key) => {
  if (isNative()) {
    await Preferences.remove({ key });
  } else {
    localStorage.removeItem(key);
  }
};

/**
 * Push Notifications
 */
export const requestPushPermissions = async () => {
  if (!isNative()) return { granted: false };

  try {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive === 'granted') {
      await PushNotifications.register();
      
      // Listen for registration token
      PushNotifications.addListener('registration', (token) => {
        console.log('[Push] Registration token:', token.value);
        // Send this token to your backend
      });

      // Listen for push notifications
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[Push] Notification received:', notification);
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('[Push] Notification action:', action);
      });

      return { granted: true };
    }

    return { granted: false };
  } catch (error) {
    console.error('[Push] Permission error:', error);
    return { granted: false };
  }
};

/**
 * Local Notifications (for reminders, session completion, etc.)
 */
export const scheduleLocalNotification = async ({
  id,
  title,
  body,
  scheduleAt,
  extra = {}
}) => {
  if (!isNative()) {
    // Web fallback using Notification API
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
    return;
  }

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title,
          body,
          schedule: { at: scheduleAt },
          extra,
          sound: 'default',
          smallIcon: 'ic_stat_icon',
        },
      ],
    });
    console.log('[LocalNotification] Scheduled:', title);
  } catch (error) {
    console.error('[LocalNotification] Error:', error);
  }
};

export const cancelLocalNotification = async (id) => {
  if (!isNative()) return;
  await LocalNotifications.cancel({ notifications: [{ id }] });
};

/**
 * Keep screen awake during meditation sessions
 */
export const keepScreenAwake = async (enable = true) => {
  // This would require @capacitor-community/keep-awake plugin
  // For now, we'll handle this on a per-platform basis later
  console.log('[Native] Keep screen awake:', enable);
};

export default {
  isNative,
  getPlatform,
  isIOS,
  isAndroid,
  isWeb,
  initializeNativeApp,
  hapticLight,
  hapticMedium,
  hapticHeavy,
  hapticSuccess,
  setStorageItem,
  getStorageItem,
  removeStorageItem,
  requestPushPermissions,
  scheduleLocalNotification,
  cancelLocalNotification,
  keepScreenAwake,
};
